import React, { useState } from 'react';
import { T } from '../shared/constants';
import { ItemFrame, Instruction, CheckButton, ResultCard, partColors } from './ItemChrome';
import { buildStudentAnswer, isCorrectResponse, feedbackFor } from './naplanItems';

/** `multi` — select ALL that apply. Response: sorted index array. */
export default function MultiRenderer({ question, studentAnswer, state, showExplanation, onSubmit, onAskLumi, onExplainVideo }) {
  const isAnswered = studentAnswer != null;
  const [draft, setDraft] = useState([]);
  const chosen = isAnswered && Array.isArray(studentAnswer.rawAnswer) ? studentAnswer.rawAnswer : draft;
  const answers = question.answers || [];
  const isCorrect = isAnswered ? (typeof studentAnswer.isCorrect === 'boolean' ? studentAnswer.isCorrect : isCorrectResponse(question, chosen)) : false;

  function toggle(i) {
    if (isAnswered) return;
    setDraft(d => (d.includes(i) ? d.filter(x => x !== i) : [...d, i].sort((a, b) => a - b)));
  }

  function submit() {
    if (isAnswered || draft.length === 0) return;
    onSubmit(buildStudentAnswer(question, [...draft].sort((a, b) => a - b)));
  }

  return (
    <ItemFrame question={question} state={state} isAnswered={isAnswered}>
      <Instruction>Select all the answers that apply</Instruction>
      <div role="group" aria-label="Answer options" style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {(question.options || []).map((opt, i) => {
          const on = chosen.includes(i);
          let status = on ? 'selected' : null;
          let icon = null;
          if (isAnswered) {
            const isAns = answers.includes(i);
            status = isAns && on ? 'right' : on ? 'wrong' : isAns ? 'missed' : null;
            icon = status === 'right' ? '✓' : status === 'wrong' ? '✗' : status === 'missed' ? '✓' : null;
          }
          const c = partColors(status);
          return (
            <button
              key={i}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-disabled={isAnswered}
              data-status={status || 'none'}
              onClick={() => toggle(i)}
              style={{
                background: c.bg, border: `1.5px ${status === 'missed' ? 'dashed' : 'solid'} ${c.border}`, borderRadius: 12,
                padding: "14px 18px", textAlign: "left", cursor: isAnswered ? "default" : "pointer",
                display: "flex", alignItems: "center", gap: 12, transition: "all .2s",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 8, background: on ? c.border + "33" : T.surface, border: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: c.color, flexShrink: 0 }}>
                {icon || (on ? '✓' : ["A","B","C","D","E","F"][i])}
              </span>
              <span style={{ fontSize: 15, color: c.color, fontWeight: on || status === 'missed' ? 600 : 400, lineHeight: 1.5 }}>{opt != null ? String(opt) : ""}</span>
              {status === 'missed' && <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: "0.05em" }}>Missed</span>}
            </button>
          );
        })}
      </div>
      {!isAnswered && <CheckButton mode={state.mode} disabled={draft.length === 0} onClick={submit} />}
      <ResultCard
        question={question} mode={state.mode} isAnswered={isAnswered} isCorrect={isCorrect}
        showExplanation={showExplanation} feedback={isAnswered && !isCorrect ? feedbackFor(question, chosen) : null}
        onAskLumi={onAskLumi} onExplainVideo={onExplainVideo}
      />
    </ItemFrame>
  );
}
